import { getBaseRequestValues } from '@src/api/helpers';
import { GetJpaOrgData, JpaOrg } from '@src/api/services/jpa/types';
import { authStore, useRequest, useModal, useNonPcn, useFormStore } from '@src/stores';
import {
  FileTypes,
  NprResultTypes,
  PcnResultTypes,
  RequestType,
  RequestTypeStringIds,
  RequestTypeStringsLookup,
} from '@src/utils/enums';
import { getAppStore } from '@src/utils/store-reference';
import { toast } from 'react-toastify';

export const enum ResultRequests {
  PermitRequired = NprResultTypes.PermitRequired,
  PreApplication = PcnResultTypes.PreApplication,
  JurisdictionalDetermination = NprResultTypes.JurisdictionalDetermination,
}

export const enum ResultLetterDownload {
  NonPcnLetter = PcnResultTypes.NonPcnLetter,
  NprExemption = NprResultTypes.NprExemption,
  NprLetter = NprResultTypes.NprLetter,
}

export const resultTypeMapping: { [key: string]: string } = {
  [NprResultTypes.PermitRequired]: RequestTypeStringIds.PermitApplication,
  [PcnResultTypes.PcnRequired]: RequestTypeStringIds.PreconstructionNotification,
  [NprResultTypes.PreApplication]: RequestTypeStringIds.PreApplication,
  [NprResultTypes.JurisdictionalDetermination]: RequestTypeStringIds.JurisdictionalDetermination,
};

const downloadFileSuffix = {
  [ResultLetterDownload.NonPcnLetter]: 'No_PCN_Required_All_Project_Information.pdf',
  [ResultLetterDownload.NprExemption]: 'NPR_All_Project_Information.pdf',
  [ResultLetterDownload.NprLetter]: 'NPR_All_Project_Information.pdf',
};

const createData = {
  [NprResultTypes.PermitRequired]: {
    id: RequestTypeStringIds.PermitApplication,
    successMessage: 'Permit request created successfully',
  },
  [PcnResultTypes.PcnRequired]: {
    id: RequestTypeStringIds.PreconstructionNotification,
    successMessage: 'Permit request created successfully',
  },
  [NprResultTypes.PreApplication]: {
    id: RequestTypeStringIds.PreApplication,
    successMessage: 'Pre-application request created successfully',
  },
  NonPcn: {
    id: RequestTypeStringIds.NonPreconstructionNotification,
    successMessage: `${RequestType.NonPreconstructionNotification} request created successfully`,
  },
  [NprResultTypes.JurisdictionalDetermination]: {
    id: RequestTypeStringIds.JurisdictionalDetermination,
    successMessage: 'Jurisdictional Determination request created successfully',
  },
};

const buildBaseRequestPayload = (requestTypeId: string, orgData?: GetJpaOrgData) => {
  const store = getAppStore();
  const formData = store.selectRequestFormData();

  const baseValues = getBaseRequestValues({
    hasPreviousFileNumber: !!formData.hasPreviousFileNumber,
    location: { ...formData.location },
    natureOfActivity: formData.natureOfActivity || '',
    nearestWaterbody: formData.nearestWaterbody || '',
    parcelNumber: formData.parcelNumber || '',
    previousFileNumbers: formData.previousFileNumbers || [],
    projectDescription: formData.projectPurpose || '',
    projectID: formData.projectID,
    projectName: formData.projectName || '',
    projectPurpose: formData.projectPurpose || '',
    requestTypeId,
    siteDirections: formData.siteDirections || '',
  });

  // Check if Pre-App or Permit has JPA
  const hasJpa = orgData
    ? orgData?.filter((org: JpaOrg) => org.validRequestTypes.includes(RequestTypeStringsLookup[requestTypeId]))
        ?.length > 0
    : false;

  if (requestTypeId !== RequestTypeStringIds.JurisdictionalDetermination) {
    return {
      project: baseValues,
      jpaOrgIds: hasJpa ? orgData?.map((org) => org?.jpaOrgID) : [],
    };
  }

  return {
    project: {
      ...baseValues,
      request: {
        requestType: RequestTypeStringsLookup[requestTypeId],
        jdRequests: [{ requestType: RequestTypeStringsLookup[requestTypeId] }],
      },
    },
    jpaOrgIds: hasJpa ? orgData?.map((org) => org?.jpaOrgID) : [],
  };
};

export const handleCreateRequest = (result: ResultRequests, orgData: GetJpaOrgData | undefined) => {
  const requestTypeId = createData[result].id;
  const projectMappedValues = buildBaseRequestPayload(requestTypeId, orgData);

  const store = getAppStore();

  const addFunctions = {
    [ResultRequests.PreApplication]: store.doAddPreApp,
    [ResultRequests.JurisdictionalDetermination]: store.doAddJD,
    [ResultRequests.PermitRequired]: store.doAddPermit,
  };

  const addFunction = addFunctions[result];

  store.doFetchGPPermitsByDistrict();
  useRequest.getState().setSelectedRequestId(requestTypeId);
  useRequest.getState().resetActiveSection();

  addFunction(projectMappedValues);
  toast.success(createData[result].successMessage);
};

export const handleDownloadLetter = (result: ResultLetterDownload) => {
  const store = getAppStore();
  const user = authStore.getCurrentUser();
  const projectId = store.selectProjectID();
  const requestId = store.selectRequestID();
  const version = store.selectVersion();

  const filePrefix = `${user?.profile?.family_name}_${projectId}_${requestId}_${version}`;
  const fileSuffix = downloadFileSuffix[result];
  const fileName = `Generated_${filePrefix}_${fileSuffix}`;

  store.doDownloadFile(fileName, FileTypes.Request, null, 'Generated');
};

export const handleContactDistrict = () => {
  window.open('https://regulatory.ops.usace.army.mil/offices/', '_blank');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleAddNonPcn = (addNonPcn: (data: any) => void) => {
  const requestTypeId = createData.NonPcn.id;
  useNonPcn.getState().resetNonPcnState();
  const payload = buildBaseRequestPayload(requestTypeId);

  addNonPcn(payload);

  const requestStore = useRequest.getState();
  requestStore.resetRequest();
  requestStore.setSelectedRequestId(requestTypeId);
  requestStore.resetActiveSection();
  useFormStore.getState().resetToDefaults();
  useModal.getState().closeModal();

  toast.success(createData.NonPcn.successMessage);
};
